<template>
  <MapWrapper
    v-if="hasLocation || bbox"
    :options="options"
    @load="loadMap"
    @click="$emit('click', $event)"
    @markerMove="$emit('markerMove', $event)"
    :sources="sources"
    :layers="layers"
    :marker="hasLocation"
  />
</template>
<script setup>
const props = defineProps({
  location: {
    type: Object,
    required: true,
  },
  zoom: {
    type: Number,
    default: 9,
  },
  bbox: {
    type: Array,
  },
  source: {
    type: Object,
    required: true,
  },
  layers: {
    type: Array,
    default: [],
  },
  lineLayer: {
    type: String,
  },
  labelLayer: {
    type: String,
  },
  labelField: {
    type: String,
  },
  featureId: {
    type: [Array, String],
  },
  options: {
    type: Object,
    default: {},
  },
  filter: {
    type: Array,
  },
});
const emit = defineEmits(["click", "markerMove", "load"]);
let map;
const hasLocation = computed(() => {
  return isNumber(props.location?.latitude) && isNumber(props.location?.longitude);
});
const options = computed(() => {
  return {
    zoom: props.zoom,
    center: hasLocation.value ? [props.location.longitude, props.location.latitude] : undefined,
    // if specified, overrides center and zoom
    bounds: props.bbox,
    fitBoundsOptions: { padding: 20 },
    ...props.options,
  };
});
const sources = computed(() => [props.source]);
const layers = computed(() => {
  if (!props.source?.id) {
    return props.layers;
  }
  const activeLayers = [
    {
      id: "features-line",
      type: "line",
      source: props.source.id,
      "source-layer": props.lineLayer,
      paint: {
        "line-opacity": 0.7,
        "line-width": ["interpolate", ["linear"], ["zoom"], 2, 0, 8, 1, 10, 2],
        "line-color": "#1e86e9",
      },
      ...(props.filter && { filter: props.filter }),
    },
    {
      id: "features-fill",
      type: "fill",
      source: props.source.id,
      "source-layer": props.lineLayer,
      paint: {
        "fill-opacity": 0.1,
        "fill-color": "#aaa",
      },
      ...(props.filter && { filter: props.filter }),
    },
    ...props.layers,
  ];
  if (props.labelLayer) {
    activeLayers.push({
      id: "features-labels",
      type: "symbol",
      source: props.source.id,
      "source-layer": props.labelLayer,
      layout: {
        "text-font": ["Noto Sans Bold"],
        "text-field": ["get", props.labelField],
        "text-size": ["interpolate", ["linear"], ["zoom"], 2, 12, 12, 16],
        "text-justify": "auto",
        "text-variable-anchor": ["center", "top", "bottom", "left", "right"],
        "symbol-sort-key": ["*", -1, ["number", ["get", "ALAND"]]],
        "text-max-width": 8,
      },
      paint: {
        "text-color": "#1e86e9",
        "text-halo-color": "#fff",
        "text-halo-width": 1,
      },
      ...(props.filter && { filter: props.filter }),
    });
  }
  if (props?.featureId && props.source?.promoteId) {
    activeLayers.push({
      id: "features-fill-selected",
      type: "fill",
      source: props.source.id,
      "source-layer": props.lineLayer,
      paint: {
        "fill-opacity": 0.3,
        "fill-color": "#1e86e9",
      },
      filter: ["in", props.source.promoteId, ...(Array.isArray(props.featureId) ? props.featureId : [props.featureId])],
    });
  }
  return activeLayers;
});
let popup;
async function loadMap(mapValue) {
  emit("load", mapValue);
  map = mapValue;
  // already loaded by Map.vue
  const { Popup } = await loadMaplibre();
  popup = new Popup({
    closeButton: false,
    closeOnClick: false,
  });
  map.on("mousemove", "features-fill", (e) => {
    map.getCanvas().style.cursor = "pointer";
    const names = e.features.map((f) => f.properties[props.labelField]);
    if (names) {
      const description = names.join(", ");
      popup.setLngLat(e.lngLat).setHTML(description).addTo(map);
    }
  });

  // Change it back to a pointer when it leaves.
  map.on("mouseleave", "features-fill", () => {
    map.getCanvas().style.cursor = "";
    popup?.remove();
  });
}

watch(
  () => props.bbox,
  (oldProps, newProps) => {
    // if the bbox changes, fit the map to the new bounds
    if (map && newProps?.bbox && !isEqual(oldProps?.bbox, newProps?.bbox)) {
      console.log("fitting bounds", newProps.bbox);
      map.fitBounds(newProps.bbox, { padding: 20 });
    }
  }
);
</script>
